'use client'

import {
  type AuthMeResponse,
  applicationApi
} from '@/stores/api/application-api'
import { useAppDispatch } from '@/utils/redux-hooks'

import { createContext, useState } from 'react'

/**
 * The server-resolved auth payload (getAuthMe), provided synchronously so SSR
 * and the first client paint read identical permissions.
 *
 * `null` when no provider is mounted above (routes outside the authed (apps)
 * layout) — usePermissions then falls back to the RTK Query result alone.
 */
export const InitialAuthMeContext = createContext<AuthMeResponse | null>(null)

/**
 * Provides the server-resolved permissions to client components two ways:
 *
 * 1. Synchronously, via React context — this is the source of truth for the
 *    first render. useSession()/RTK Query are empty during SSR, so anything
 *    branching its render tree on can() (the sidebar footer, app switcher)
 *    used to render the "no permissions" tree on the server and the "has
 *    permissions" tree on the client → hydration mismatch. Context carries the
 *    value through the SSR pass synchronously, so both sides agree.
 *
 * 2. Into the RTK Query cache, via upsertQueryData — so the live
 *    useGetAuthMeQuery (focus refetch, tag invalidation after admin permission
 *    mutations) starts from the server value and takes over once it resolves,
 *    rather than refetching from scratch on mount.
 *
 * Mount once inside the authenticated (apps) layout. useState's lazy
 * initializer runs synchronously during the first render — before any child
 * renders — and upsertQueryData is idempotent, so strict mode's double-invoke
 * is harmless.
 */
export function PermissionsProvider({
  initialData,
  children
}: {
  initialData: AuthMeResponse
  children: React.ReactNode
}) {
  const dispatch = useAppDispatch()

  useState(() => {
    dispatch(
      applicationApi.util.upsertQueryData('getAuthMe', undefined, initialData)
    )
    return null
  })

  return (
    <InitialAuthMeContext.Provider value={initialData}>
      {children}
    </InitialAuthMeContext.Provider>
  )
}
